import { useCallback, useEffect, useEffectEvent, useMemo, useState, type ComponentType } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector } from '@store/hooks';
import { useCanAccess } from '@permissions/useFeatures';
import { openCreateDialog } from '@store/slices/dialog.slice';
import { selectDialogOpen } from '@store/selectors/dialog.selectors';
import { IssueFormDialog } from '../IssuesPage/Dialog/IssueFormDialog';
import { useActivityFilters } from './hooks/useActivityFilters';
import { useLinkedFilters } from './hooks/useLinkedFilters';
import { useAlertDrillDown } from './hooks/useAlertDrillDown';
import { useUnifiedList } from './hooks/useUnifiedList';
import { useBootstrapSchedule } from './hooks/useBootstrapSchedule';
import { useActivitiesFromSchedule } from './hooks/useActivitiesFromSchedule';
import { tradeLabel } from '@utils/tradeLabel';
import { readFiltersPanelOpen, writeFiltersPanelOpen } from './filtersPrefs';
import { CalendarIcon, GridIcon, ListIcon, PlusIcon } from '@utils/icons';
import { useIssueRoute, useOpenIssue } from './hooks/useIssueRoute';
import { useActivityUrlSync, useLinkedActivitySchedule, useOpenTaskPanel } from './hooks/useActivityPanel';
import { useReadState } from '@hooks/useReadState';
import { FilterBar } from './FilterBar/FilterBar';
import { FilterPanel } from './FilterBar/FilterPanel';
import { DateNavigator } from './DateNavigator/DateNavigator';
import { useDateWindow } from './hooks/useDateWindow';
import { KindScopeTabs } from './FilterBar/KindScopeTabs';
import { CardList } from './CardList/CardList';
import { CardListSkeleton } from './CardList/CardListSkeleton';
import { EmptyBoard } from './EmptyBoard/EmptyBoard';
import { FirstIssueShortcut } from './CardList/FirstIssueShortcut';
import { linkedTaskTitle } from './model/issueDisplay';
import { ActivityDrawer } from './Drawer/ActivityDrawer';
import { RiskAssessment } from './Drawer/RiskAssessment';
import { RightRail } from './RightRail/RightRail';
import { Button } from '@components/ui/Button/Button';
import { DataUnavailable } from '@components/DataUnavailable/DataUnavailable';
import { Tabs } from '@components/ui/Tabs/Tabs';
import { useActivityView, type ActivityView } from './hooks/useActivityView';
import { ProgressTracker } from './ProgressTracker/ProgressTracker';
import { useTrackerPopulation } from './ProgressTracker/useTrackerPopulation';
import { WorkPlan } from './WorkPlan/WorkPlan';
import { TaskPanel } from '../SchedulerPage/components/TaskPanel/TaskPanel';
import { useSchedulerActions, useSchedulerSite, useSchedulerPanelOpen } from '../SchedulerPage/hooks/useSchedulerStore';
import { ActivityLadderContext, usePageActivityLadder } from '../SchedulerPage/hooks/useActivityLadder';
import type { IListItem } from './model/listItem';
import { sourceOutage } from './model/sourceOutage';
import type { ISchedulerTask } from '@interfaces/IScheduler';
import styles from './ActivitiesPage.module.css';
import { ScrollArea } from '@components/ui/ScrollArea/ScrollArea';

/** The views, the Work Plan first: the week's plan, the day's feed, the
 *  area × activity matrix. */
const VIEW_TABS = [
  { id: 'workplan', labelKey: 'activities.view.workPlan', Icon: CalendarIcon },
  { id: 'list', labelKey: 'activities.view.dailyList', Icon: ListIcon },
  { id: 'tracker', labelKey: 'activities.view.progressTracker', Icon: GridIcon },
] as const satisfies readonly { id: ActivityView; labelKey: string; Icon: ComponentType<{ size?: number }> }[];

/** One empty plan for every render before the scheduler store holds the site. */
const NO_TASKS: ISchedulerTask[] = [];

export function ActivitiesPage() {
  const { t } = useTranslation();
  // siteId feeds the right-rail Monitor panels, the unified list, the cert
  // banner and the issue create/edit dialog. Empty string disables the
  // site-gated queries (they all check `enabled: !!siteId`).
  const { siteId = '' } = useParams<{ siteId: string }>();
  const dispatch = useAppDispatch();
  const dialogOpen = useAppSelector(selectDialogOpen);
  // Issue writes beyond contributing (create, edit, delete, status moves).
  const canManageIssues = useCanAccess('issues.manage');

  // A link from an assistant answer arrives with its filters in the URL; they
  // replace the saved set, then leave the URL (`useLinkedFilters`).
  const { linked, pending: linkPending } = useLinkedFilters();
  const filters = useActivityFilters(linked);
  const { view, setView } = useActivityView();
  // The filter control center — an in-flow left sidebar holding every filter
  // option; the bar itself carries only the trigger + search (see FilterBar).
  // Open by default: it's page furniture, not an overlay. The open/collapsed
  // choice persists across navigations (localStorage) so the panel comes back
  // the way the user left it — written only on an actual user toggle (same
  // idiom as the Scheduler's labelsCollapsed toggle), so an untouched
  // preference never gets baked into storage.
  const [filtersOpen, setFiltersOpen] = useState(readFiltersPanelOpen);
  const toggleFilters = useCallback(() => {
    setFiltersOpen((open) => {
      const next = !open;
      writeFiltersPanelOpen(next);
      return next;
    });
  }, []);
  const closeFilters = useCallback(() => {
    setFiltersOpen(false);
    writeFiltersPanelOpen(false);
  }, []);
  // The page's committed activities over the schedule they came from — and
  // the site's rest days, which the one date picker (below) shades by.
  const { detail, scheduleNumber, tasks, weeklyDaysOff, committed } = useActivitiesFromSchedule(siteId);
  // The page's ONE date picker: every view reads its window.
  const dates = useDateWindow(siteId, weeklyDaysOff);
  const { items, allItems, activityItems, today, restDays, activitiesLoading, issuesLoading, activitiesError, issuesError } =
    useUnifiedList(siteId, filters.filters, dates.window);
  const planLoading = activitiesLoading || issuesLoading;
  // The Progress Tracker's activities, held here so the header counts exactly
  // the columns it renders — the same selector, filters and window as the
  // Daily List's activities, narrowed only by what the tracker is.
  const tracker = useTrackerPopulation({
    siteId, activityItems, today, restDays, filters: filters.filters, window: dates.window, clear: filters.clear,
  });

  // Narrows the Daily List to exactly the members of one alert group when the
  // page was opened from the dashboard's Activity Alerts card "View in page ›"
  // link (`?alertType=&alertDay=`) — resolved against the whole feed, not the
  // date window or the saved filters, with the segment showing the alert's
  // kind for the visit (`useAlertDrillDown`). The Progress Tracker and the
  // Work Plan stay on the ordinary list — the descriptor addresses list rows.
  const drill = useAlertDrillDown(siteId, allItems, filters.filters);
  const visibleItems = drill.rows ?? items;
  // What the list shows; when it changes the list returns to its first page.
  // Never the rows array: every live refetch mints a new one for the same view.
  const listResetKey = JSON.stringify([
    siteId,
    scheduleNumber,
    dates.window?.from,
    dates.window?.to,
    filters.filters,
    drill.isActive && [drill.type, drill.day],
  ]);

  // Empty-state conditions are computed on the UNFILTERED list, so a generic
  // filter that empties the visible items keeps the plain "no match" text rather
  // than the guided onboarding state (spec: 2026-07-23-issues-empty-state-design.md).
  const listSettled = !activitiesLoading && !issuesLoading && !activitiesError && !issuesError;
  const hasAnyActivity = allItems.some((i) => i.kind === 'activity');
  const hasAnyIssue = allItems.some((i) => i.kind === 'issue');
  // The item-type scope narrowed to issues only — "show me the issues".
  const listTypes = drill.types ?? filters.filters.types;
  const issuesOnlyFilter = listTypes.length === 1 && listTypes[0] === 'issue';
  // A failed feed darkens only the list it feeds: what the scope asks of it.
  const outage = sourceOutage({ activity: activitiesError, issue: issuesError }, listTypes);
  // Guided empty state: the whole board is empty, OR the board is narrowed to
  // issues-only and there are none yet. Both read to the user as "there are no
  // issues here" — the same onboarding moment, so show the same guided card.
  const showGuidedEmpty = listSettled && (allItems.length === 0 || (issuesOnlyFilter && !hasAnyIssue));
  // Site state, not filter state: the shortcut stays while filters are active.
  const showFirstIssueShortcut = listSettled && hasAnyActivity && !hasAnyIssue;

  // Issues keep their own URL-driven drawer (`?openIssue=<id>`). Activities
  // open the shared Scheduler TaskPanel instead (see below), so the detail
  // surface is identical to the one the Scheduler shows on task click.
  const { open, close } = useIssueRoute();
  const { openItem } = useOpenIssue(allItems);

  // Distinct profession trades present in the loaded schedule, sorted by their
  // display label — the option list for the FilterBar's Profession filter.
  const professionOptions = useMemo(() => {
    const ids = new Set<string>();
    for (const task of tasks) if (task.profession?.tradeId) ids.add(task.profession.tradeId);
    return [...ids].sort((a, b) =>
      (tradeLabel(t, a) || a).localeCompare(tradeLabel(t, b) || b, 'ja'),
    );
  }, [tasks, t]);
  const { closePanel, updateTask } = useSchedulerActions();
  const openTask = useOpenTaskPanel();
  const site = useSchedulerSite(siteId);
  const panelOpen = useSchedulerPanelOpen();
  // The drawer is the Scheduler's TaskPanel: it reads Trusstor's word for the
  // task — its rung, the done suggestion, each area's status — from the same
  // feed, wired the same way.
  const { store: activityLadder } = usePageActivityLadder(siteId || undefined, site?.tasks ?? NO_TASKS);
  // Bootstrap the scheduler store for this schedule so the shared TaskPanel has
  // live task data to render (and its status/progress/location tabs can persist).
  useBootstrapSchedule(siteId, detail, scheduleNumber);

  // A linked activity (a chat mention, a pasted link) may be a promise from
  // another of the site's schedules; the page moves there first, and the sync
  // below waits — judged against this schedule, the link would clear as one
  // the page does not hold.
  const movingToLinked = useLinkedActivitySchedule({ siteId, feed: committed, onScreen: scheduleNumber });
  // `?openActivity=` ⇄ the TaskPanel. After the bootstrap above, so a link
  // opens onto this schedule.
  useActivityUrlSync({ tasks: detail?.tasks, paused: linkPending || movingToLinked });

  const readState = useReadState(siteId || null);

  const handleOpen = (item: IListItem) => {
    readState.markSeen(item.key, item.commentCount);
    if (item.kind === 'activity') {
      // Open the shared drawer against the scheduler store (Overview tab).
      openTask(item.raw.activity?.id ?? item.key.replace(/^act:/, ''));
    } else {
      open(item.key); // issue → its own URL drawer
    }
  };

  // Structural edits (Edit tab) are hidden on the Activities drawer, so onSave is
  // never triggered here; provide the correct handler regardless for safety.
  const handleTaskPanelSave = useCallback(
    (task: ISchedulerTask) => updateTask({ siteId, task }),
    [siteId, updateTask],
  );

  // The Risk Assessment shown at the top of the drawer's Overview tab. Reuses the
  // already-computed per-item risk from the unified list (no recompute), keyed to
  // the open task, and injected via the panel's slot so the Scheduler stays clean.
  const openActivityItem = panelOpen?.taskId
    ? allItems.find((i) => i.key === `act:${panelOpen.taskId}`)
    : undefined;
  const riskSlot = openActivityItem ? (
    <RiskAssessment onTime={openActivityItem.onTimeRisk} safety={openActivityItem.safety} />
  ) : undefined;

  // Card highlight: an activity is "open" via the store panel, an issue via the URL.
  const openKey = panelOpen?.taskId ? `act:${panelOpen.taskId}` : openItem?.key;

  // Suppress the unread UI until the read state has loaded — before it resolves
  // every item looks unseen, which would flash dots and let "Mark all as read"
  // fire against a not-yet-known server state.
  const hasUnread =
    !readState.isLoading &&
    visibleItems.some(
      (item) => readState.isUnseen(item.key) || readState.unreadComments(item.key, item.commentCount) > 0,
    );

  // While an item's drawer is open, keep its comments marked read as new ones
  // arrive (sent/received in the chat) — otherwise its card flashes an unread
  // dot/count behind the drawer the user is actively reading. useEffectEvent so
  // the latest readState/openItem are read without making them effect deps (the
  // effect should only re-run when the open item or its comment count changes).
  const markOpenItemSeen = useEffectEvent(() => {
    if (openItem && readState.unreadComments(openItem.key, openItem.commentCount) > 0) {
      readState.markSeen(openItem.key, openItem.commentCount);
    }
  });
  useEffect(() => {
    markOpenItemSeen();
  }, [openItem?.key, openItem?.commentCount]);

  return (
    <div className={styles.page}>
      {/* ONE header row: the filter trigger + search (the page's item count
          beside it) and the page's date picker at the left, the view tabs at
          the right. */}
      <div className={styles.header}>
        <div className={styles.headerLeft}>
          <div className={styles.filterSlot}>
            <FilterBar
              f={filters}
              panelOpen={filtersOpen}
              onTogglePanel={toggleFilters}
              searchValue={filters.filters.search}
              onSearchChange={filters.setSearch}
            >
              {/* Each view counts what it renders: the Daily List its cards
                  (an alert drill-down's members included), the Work Plan the
                  items the filters and the date window leave; the tracker,
                  activities-only, counts the activities it lays out. */}
              {!planLoading && (
                <span className={styles.total}>
                  {view === 'tracker'
                    ? t('activities.totalActivities', { count: tracker.activities.length })
                    : t('activities.total', { count: (view === 'list' ? visibleItems : items).length })}
                </span>
              )}
            </FilterBar>
          </div>
          {dates.window && (
            <DateNavigator
              window={dates.window}
              today={dates.today}
              weeks={dates.weeks}
              onPage={dates.page}
              onJump={dates.jumpTo}
              onWeeksChange={dates.setWeeks}
            />
          )}
        </div>
        <Tabs.Root value={view} onValueChange={(next) => setView(next as ActivityView)} className={styles.viewTabs}>
          <Tabs.List aria-label={t('activities.view.label')} className={styles.viewTabList}>
            {VIEW_TABS.map((tab) => (
              <Tabs.Tab key={tab.id} value={tab.id}>
                <tab.Icon size={16} />
                {t(tab.labelKey)}
              </Tabs.Tab>
            ))}
            <Tabs.Indicator />
          </Tabs.List>
        </Tabs.Root>
      </div>
      {/* Content row: the filter control center is an in-flow LEFT column —
          the active view's body reflows beside it when it opens/collapses. */}
      <div className={styles.content}>
        <FilterPanel
          open={filtersOpen}
          onClose={closeFilters}
          f={filters}
          view={view}
          professionOptions={professionOptions}
        />
        {view === 'tracker' ? (
          <div className={styles.trackerBody}>
            <ProgressTracker siteId={siteId} population={tracker} loading={activitiesLoading} />
          </div>
        ) : view === 'workplan' ? (
          <div className={styles.trackerBody}>
            <WorkPlan
              siteId={siteId}
              items={allItems}
              today={dates.today}
              window={dates.window}
              loading={planLoading}
              outage={sourceOutage({ activity: activitiesError, issue: issuesError }, filters.filters.types)}
              f={filters}
            />
          </div>
        ) : (
          <ScrollArea className={styles.body} viewportClassName={styles.bodyContent}>
            <div className={styles.left}>
              {drill.isActive && (
                <button type="button" className={styles.alertFilterChip} onClick={drill.clear}>
                  {t('activities.alertFilter.chip', {
                    type: t(`dashboard.activityAlerts.types.${drill.type}`),
                    day: drill.day,
                  })}
                  <span aria-hidden="true"> ✕</span>
                </button>
              )}
              {/* UI trial: the actions take the search bar's old slot. Styled to
                  match the Scheduler toolbar's action buttons (its todayBtn /
                  primaryBtn recipes) so the two operations surfaces read alike.
                  The kind scope leads the row — a switcher on top of a feed
                  narrows the feed (inbox-tabs pattern), and it exists only
                  here because the tracker is activities-only. */}
              <div className={styles.listActions}>
                <KindScopeTabs f={filters} types={drill.types} />
                {hasUnread && (
                  <Button
                    variant="secondary"
                    size="sm"
                    className={styles.markAllReadBtn}
                    onClick={() =>
                      readState.markAllRead(visibleItems.map((i) => ({ key: i.key, commentCount: i.commentCount })))
                    }
                  >
                    {t('activities.markAllRead')}
                  </Button>
                )}
                {!showGuidedEmpty && canManageIssues && (
                  <Button
                    variant="primary"
                    size="sm"
                    className={styles.createBtn}
                    onClick={() => dispatch(openCreateDialog())}
                  >
                    <PlusIcon size={14} />
                    {t('activities.createIssue')}
                  </Button>
                )}
              </div>
              {outage === 'all' ? (
                <DataUnavailable />
              ) : planLoading || drill.resolving ? (
                <CardListSkeleton />
              ) : showGuidedEmpty ? (
                <EmptyBoard onCreateIssue={canManageIssues ? () => dispatch(openCreateDialog()) : undefined} />
              ) : (
                <>
                  {outage && <DataUnavailable compact message={t(`activities.outage.${outage}`)} />}
                  <CardList
                    items={visibleItems}
                    onOpen={handleOpen}
                    readState={readState}
                    openKey={openKey}
                    loading={readState.isLoading}
                    canManageIssues={canManageIssues}
                    resetKey={listResetKey}
                  />
                  {showFirstIssueShortcut && canManageIssues && (
                    <FirstIssueShortcut onClick={() => dispatch(openCreateDialog())} />
                  )}
                </>
              )}
            </div>
            <RightRail siteId={siteId} className={styles.rail} />
          </ScrollArea>
        )}
      </div>
      {/* Issues keep their own drawer; activities use the shared TaskPanel below. */}
      {openItem?.kind === 'issue' && (
        <ActivityDrawer
          item={openItem}
          onClose={close}
          linkedTaskTitle={linkedTaskTitle(openItem.raw.issue, allItems)}
        />
      )}
      {site && (
        <ActivityLadderContext.Provider value={activityLadder}>
          <TaskPanel
            open={panelOpen !== null}
            mode={panelOpen?.mode ?? 'edit'}
            data={site}
            scheduleNumber={scheduleNumber}
            taskId={panelOpen?.taskId}
            allowStructureEdit={false}
            overviewSlot={riskSlot}
            onClose={closePanel}
            onSave={handleTaskPanelSave}
          />
        </ActivityLadderContext.Provider>
      )}
      {dialogOpen && <IssueFormDialog />}
    </div>
  );
}
